import { FC, HTMLInputTypeAttribute, ChangeEvent, useContext } from "react";
import { useTranslation } from "react-i18next";

import TextField, { TextFieldProps } from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import IconButton from "@mui/material/IconButton";
import ClearIcon from "@mui/icons-material/Clear";

import FormContext from "./FormContext";

interface InputProps extends Omit<TextFieldProps, "onChange" | "error"> {
  label: string;
  value: string;
  error?: string | null;
  disabled?: boolean;
  type?: HTMLInputTypeAttribute;
  onChange?: (value: string) => void;
  clearable?: boolean;
}

export const Input: FC<InputProps> = ({
  label,
  value,
  error,
  disabled = false,
  type = "text",
  onChange = () => {},
  clearable = false,
  slotProps,
  ...otherProps
}) => {
  const { t } = useTranslation();

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(e.target?.value);
  };

  const handleClear = (e: React.MouseEvent) => {
    e.preventDefault();
    onChange("");
    // Maintain focus on the input after clearing
    const input = e.currentTarget.closest(".MuiTextField-root")?.querySelector("input");
    if (input) {
      input.focus();
    }
  };

  return (
    <TextField
      variant="outlined"
      type={type}
      value={value || ""}
      label={label}
      error={!!error}
      disabled={disabled}
      size="small"
      helperText={error || undefined}
      onChange={handleChange}
      fullWidth
      slotProps={{
        ...slotProps,
        input: {
          ...(slotProps as { input?: object })?.input,
          endAdornment: clearable && value ? (
            <InputAdornment position="end">
              <IconButton size="small" onClick={handleClear} edge="end" aria-label={String(t("clear"))} disabled={disabled}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined,
        },
      }}
      {...otherProps}
    />
  );
};

interface InputFormProps extends Omit<TextFieldProps, "onChange"> {
  name: string;
  label: string;
  disabled?: boolean;
  type?: HTMLInputTypeAttribute;
  onChange?: (value: string) => void;
}

const InputForm: FC<InputFormProps> = ({ name, label, disabled = false, type = "text", onChange, ...otherProps }) => {
  const context = useContext(FormContext);
  const { value, error } = context.get(name);

  const handleChange = (newValue: string) => {
    context.set(name, newValue);
    onChange?.(newValue);
  };

  return (
    <Input
      label={label}
      value={value || ""}
      error={error}
      disabled={disabled}
      type={type}
      onChange={handleChange}
      {...otherProps}
    />
  );
};

export default InputForm;
